/**
 * Firestore helpers for Catarot — built into public/js/firebase-bundle.js (see npm run build:firebase).
 * - catsMagic/current — 최신 편집본 (post, updatedAt)
 * - catsMagicEpisodes/{slug} — 연재 회차별 공개 스냅샷 (동일 post 형식, 문서 ID = URL 슬러그)
 *
 * 설정은 vite.firebase.config.ts 에서 __CATAROT_FIREBASE_CONFIG__ 로 주입됩니다 (import.meta.env 라이브러리 빌드 이슈 회피).
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, type User } from 'firebase/auth';

/** Must match the email in firestore.rules — only this account can write. */
const CATS_MAGIC_ADMIN_EMAIL = 'johntristan720110@gmail.com';

declare const __CATAROT_FIREBASE_CONFIG__: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
};

const firebaseConfig: typeof __CATAROT_FIREBASE_CONFIG__ = __CATAROT_FIREBASE_CONFIG__;

export function isFirebaseConfigured(): boolean {
    return Boolean(
        firebaseConfig.apiKey &&
            firebaseConfig.projectId &&
            firebaseConfig.appId &&
            firebaseConfig.authDomain
    );
}

let app: FirebaseApp | null = null;

function getFirebaseApp(): FirebaseApp {
    if (!isFirebaseConfigured()) {
        throw new Error('Firebase env vars are missing. Copy .env.example to .env and run npm run build:firebase.');
    }
    if (!app) {
        app = initializeApp(firebaseConfig);
    }
    return app;
}

function isAdminUser(user: User | null): boolean {
    return Boolean(user && user.emailVerified && (user.email || '').toLowerCase() === CATS_MAGIC_ADMIN_EMAIL);
}

/** Resolves with the admin email, or null when the chosen Google account is not the admin. */
export async function signInCatsMagicAdmin(): Promise<string | null> {
    const auth = getAuth(getFirebaseApp());
    if (isAdminUser(auth.currentUser)) {
        return auth.currentUser!.email;
    }
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const { user } = await signInWithPopup(auth, provider);
    if (isAdminUser(user)) {
        return user.email;
    }
    await signOut(auth);
    return null;
}

export async function signOutCatsMagicAdmin(): Promise<void> {
    await signOut(getAuth(getFirebaseApp()));
}

/** Calls back once auth state is known and on every change, with whether the admin is signed in. */
export function onCatsMagicAdminChanged(callback: (isAdmin: boolean) => void): () => void {
    return onAuthStateChanged(getAuth(getFirebaseApp()), (user) => callback(isAdminUser(user)));
}

export async function fetchCatsMagicPost(): Promise<unknown | null> {
    const db = getFirestore(getFirebaseApp());
    const snap = await getDoc(doc(db, 'catsMagic', 'current'));
    if (!snap.exists()) {
        return null;
    }
    const data = snap.data() as Record<string, unknown>;
    if (data && data.post != null) {
        return data.post;
    }
    return data;
}

export async function saveCatsMagicPost(post: unknown): Promise<void> {
    const db = getFirestore(getFirebaseApp());
    await setDoc(
        doc(db, 'catsMagic', 'current'),
        {
            post,
            updatedAt: serverTimestamp()
        },
        { merge: true }
    );
}

/** URL·문서 ID에 쓸 회차 슬러그 (소문자, a-z0-9- 만, 최대 120자). */
export function sanitizeCatsMagicEpisodeId(raw: string): string | null {
    const s = String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    if (!s || s.length > 120) {
        return null;
    }
    return s;
}

export async function fetchCatsMagicEpisode(slug: string): Promise<unknown | null> {
    const id = sanitizeCatsMagicEpisodeId(slug);
    if (!id) {
        return null;
    }
    const db = getFirestore(getFirebaseApp());
    const snap = await getDoc(doc(db, 'catsMagicEpisodes', id));
    if (!snap.exists()) {
        return null;
    }
    const data = snap.data() as Record<string, unknown>;
    if (data && data.post != null) {
        return data.post;
    }
    return data;
}

export async function saveCatsMagicEpisode(slug: string, post: unknown): Promise<void> {
    const id = sanitizeCatsMagicEpisodeId(slug);
    if (!id) {
        return;
    }
    const db = getFirestore(getFirebaseApp());
    await setDoc(
        doc(db, 'catsMagicEpisodes', id),
        {
            post,
            updatedAt: serverTimestamp()
        },
        { merge: true }
    );
}

export async function deleteCatsMagicEpisode(slug: string): Promise<void> {
    const id = sanitizeCatsMagicEpisodeId(slug);
    if (!id) {
        return;
    }
    const db = getFirestore(getFirebaseApp());
    await deleteDoc(doc(db, 'catsMagicEpisodes', id));
}

export type CatsMagicEpisodeListItem = {
    slug: string;
    updatedAtMs: number;
    titleKo: string;
    titleEn: string;
    titleJa: string;
    excerptKo: string;
    excerptEn: string;
    excerptJa: string;
    publishedAt: string;
    imageUrl: string;
};

/** Newest-first series order: chapter number DESC → publishedAt DESC → slug (never updatedAt). */
function compareCatsMagicEpisodesBySeriesOrder(
    a: { slug: string; publishedAt?: string },
    b: { slug: string; publishedAt?: string }
): number {
    const chapterFromSlug = (slug: string): number | null => {
        const m = String(slug || '').match(/^chapter-(\d{1,6})(?:-|$)/i);
        return m ? Number(m[1]) : null;
    };
    const chapterA = chapterFromSlug(a.slug);
    const chapterB = chapterFromSlug(b.slug);
    const numA = chapterA == null ? Number.NEGATIVE_INFINITY : chapterA;
    const numB = chapterB == null ? Number.NEGATIVE_INFINITY : chapterB;
    if (numA !== numB) return numB - numA;
    const publishedA = String(a.publishedAt || '').trim();
    const publishedB = String(b.publishedAt || '').trim();
    if (publishedA && publishedB && publishedA !== publishedB) {
        return publishedB.localeCompare(publishedA);
    }
    if (publishedA && !publishedB) return -1;
    if (!publishedA && publishedB) return 1;
    return String(a.slug || '').localeCompare(String(b.slug || ''));
}

/** 공개 연재 목록용: `catsMagicEpisodes` 전체 문서 메타 (회차/발행일 순). */
export async function listCatsMagicEpisodes(): Promise<CatsMagicEpisodeListItem[]> {
    const db = getFirestore(getFirebaseApp());
    const snap = await getDocs(collection(db, 'catsMagicEpisodes'));
    const items: CatsMagicEpisodeListItem[] = [];
    for (const d of snap.docs) {
        const slug = d.id;
        const data = d.data() as Record<string, unknown>;
        let updatedAtMs = 0;
        const u = data.updatedAt as { toDate?: () => Date } | undefined;
        if (u && typeof u.toDate === 'function') {
            try {
                updatedAtMs = u.toDate().getTime();
            } catch {
                /* ignore */
            }
        }
        const post = data.post as Record<string, unknown> | undefined;
        let titleKo = '';
        let titleEn = '';
        let titleJa = '';
        let excerptKo = '';
        let excerptEn = '';
        let excerptJa = '';
        let publishedAt = '';
        let imageUrl = '';
        if (post && post.locales && typeof post.locales === 'object') {
            const loc = post.locales as Record<string, { title?: string; excerpt?: string; bodyHtml?: string }>;
            const pickSummary = (entry?: { excerpt?: string; bodyHtml?: string }): string => {
                const ex = (entry?.excerpt || '').trim();
                if (ex) return ex;
                const plain = (entry?.bodyHtml || '')
                    .replace(/<[^>]+>/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
                return plain.slice(0, 220);
            };
            titleKo = (loc.ko?.title || '').trim();
            titleEn = (loc.en?.title || '').trim();
            titleJa = (loc.ja?.title || '').trim();
            excerptKo = pickSummary(loc.ko);
            excerptEn = pickSummary(loc.en);
            excerptJa = pickSummary(loc.ja);
        }
        if (post && typeof post.publishedAt === 'string') {
            publishedAt = post.publishedAt.trim();
        }
        if (post && typeof post.imageUrl === 'string') {
            imageUrl = post.imageUrl.trim();
        }
        items.push({
            slug,
            updatedAtMs,
            titleKo,
            titleEn,
            titleJa,
            excerptKo,
            excerptEn,
            excerptJa,
            publishedAt,
            imageUrl
        });
    }
    items.sort(compareCatsMagicEpisodesBySeriesOrder);
    return items;
}
